import React, {useEffect, useState} from 'react';
import {Dialog} from 'primereact/dialog';
import {AdminButton, AdminInputDropdown} from '../../../layout/admin';
import AdminTextArea from '../../../layout/admin/AdminTextArea';
import {Publication} from '../types/Publication';
import publicationsService from '../services/publicationsService';
import bibliographyItemsService from '../services/bibliographyItemsService';
import {handleError} from '../../../common/utils/errorHandling';
import {useTranslation} from "react-i18next";

interface BibliographyItemImportDialogProps {
  visible: boolean;
  onHide: () => void;
  onImportComplete: () => void;
}

const BibliographyItemImportDialog: React.FC<BibliographyItemImportDialogProps> = ({
  visible,
  onHide,
  onImportComplete
}) => {
  const {t} = useTranslation();
  const [publications, setPublications] = useState<Publication[]>([]);
  const [publicationId, setPublicationId] = useState('');
  const [csvContent, setCsvContent] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      fetchPublications();
    }
  }, [visible]);

  const fetchPublications = async (): Promise<void> => {
    await handleError(
      async () => {
        const publicationsData = await publicationsService.getAllPublications();
        setPublications(publicationsData);
      },
      t('admin.bibliographyItems.errors.fetching')
    );
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setLoading(true);

    await handleError(
      async () => {
        await bibliographyItemsService.importBibliographyItems(publicationId, csvContent);
        alert(t('admin.bibliographyItems.import.success'));
        handleDialogHide();
        onImportComplete();
      },
      t('admin.bibliographyItems.errors.importing'),
      () => alert(t('admin.bibliographyItems.errors.importing'))
    );

    setLoading(false);
  };

  const handleDialogHide = (): void => {
    setPublicationId('');
    setCsvContent('');
    onHide();
  };

  const publicationOptions = [
    ...publications.map(pub => ({
      label: pub.name,
      value: pub.id
    }))
  ];

  return (
    <Dialog
      header={t('admin.bibliographyItems.import.title')}
      visible={visible}
      style={{width: '50vw'}}
      onHide={handleDialogHide}
      maskClassName="bg-black bg-opacity-50"
    >
      <form onSubmit={handleSubmit}>
        <AdminInputDropdown
          value={publicationId}
          onChange={(e) => setPublicationId(e.target.value)}
          name="publicationId"
          id="publicationId"
          label={t('admin.bibliographyItems.fields.publication')}
          options={publicationOptions}
          required
        />

        <AdminTextArea
          value={csvContent}
          onChange={(e) => setCsvContent(e.target.value)}
          name="csvContent"
          id="csvContent"
          label={t('admin.bibliographyItems.import.csvContent')}
          placeholder={t('admin.bibliographyItems.import.placeholder')}
          rows={12}
          required
          className="mt-4"
        />

        <div className="text-sm text-gray-600 mt-2 mb-4">
          <strong>{t('admin.bibliographyItems.import.expectedFormat')}</strong> {t('admin.bibliographyItems.import.formatDescription')}
          <br />
          <strong>{t('admin.bibliographyItems.import.example')}</strong>
          <pre className="bg-gray-100 p-2 mt-1 rounded text-xs whitespace-pre-wrap">
            "Bularz-Różycka 2007","Ludmiła Bularz-Różycka, Nieznane rysunki Stanisława Wyspiańskiego, &lt;em&gt;„Alma Mater"&lt;/em&gt; 2007, nr 97, s. 32-37."<br/>
            "Chwalba 2002","Andrzej Chwalba, &lt;em&gt;Dzieje Krakowa&lt;/em&gt;, t. 5, &lt;em&gt;Kraków w latach 1939-1945&lt;/em&gt;, Kraków 2002."
          </pre>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <AdminButton
            type="button"
            onClick={handleDialogHide}
            variant="white"
            label={t('admin.common.actions.cancel')}
          />
          <AdminButton
            type="submit"
            variant="black"
            label={loading ? t('admin.common.actions.importing') : t('admin.common.actions.import')}
            disabled={loading}
          />
        </div>
      </form>
    </Dialog>
  );
};

export default BibliographyItemImportDialog;

